import { useState, type CSSProperties, type ReactNode } from 'react';
import type { TagCount } from '@photos/shared';
import { useNav } from '../api/hooks';
import { fmtCount, fmtTotal } from '../lib/format';
import { useLibraryState, type LibraryTab } from '../state/libraryParams';
import { Icon, type IconName } from './Icon';

export const NAVC: Record<string, string> = {
  'tab:all': '#2f6fe4',
  'tab:picks': '#e5a000',
  'tab:years': '#0ea5b7',
  'tab:regions': '#2b5bd0',
  'tab:events': '#f0731a',
  'tab:groups': '#8b5cf6',
  'tab:churches': '#0b84c6',
  'studio:albums': '#2f6fe4',
  'studio:unreleased': '#7244b8',
  'studio:recent': '#17a06a',
  'studio:restricted': '#b33a5d',
  'studio:removals': '#e0475b',
  'studio:activity': '#0ea5b7',
  'studio:trash': '#64748b',
  'studio:access': '#c2410c',
  'studio:duplicates': '#9b5de5',
};

/** Colored icon tile used by the sidebar items (Browse and Studio). */
export function Ico({ nav, icon }: { nav: string; icon: IconName }) {
  return (
    <span className="ico" style={{ '--c': NAVC[nav] ?? '#64748b' } as CSSProperties}>
      <Icon name={icon} />
    </span>
  );
}

type GroupKey = 'years' | 'regions' | 'events' | 'churches' | 'groups';

interface SidebarProps {
  open: boolean;
  onClose(): void;
}

/** Sticky left navigation; a slide-out drawer under 900px. */
export function Sidebar({ open, onClose }: SidebarProps) {
  const { state, go, onLibrary } = useLibraryState();
  const nav = useNav(state.source);
  const [openGroups, setOpenGroups] = useState<Record<GroupKey, boolean>>({
    years: true,
    regions: false,
    events: true,
    churches: false,
    groups: false,
  });
  const key = state.tags.join('|');
  const isLib = onLibrary;
  const n = nav.data;

  const toTab = (tab: LibraryTab) => {
    onClose();
    go({ tab, tags: [], picksOnly: false, orient: 'all' });
  };
  const toTags = (tags: string[]) => {
    onClose();
    go({ tab: 'all', tags, picksOnly: false });
  };
  const item = (navKey: string, icon: IconName, label: string, count: number | null, on: boolean, onClick: () => void) => (
    <button type="button" className={`sn${on ? ' on' : ''}`} onClick={onClick} aria-current={on ? 'page' : undefined}>
      <Ico nav={navKey} icon={icon} />
      <span>{label}</span>
      {count != null ? <span className="n">{fmtCount(count)}</span> : null}
    </button>
  );

  const child = (tags: string[], label: string, count: number) => (
    <button
      type="button"
      key={tags.join('|')}
      className={`sk${isLib && key === tags.join('|') ? ' on' : ''}`}
      onClick={() => toTags(tags)}
    >
      <span>{label}</span>
      <span className="n">{fmtCount(count)}</span>
    </button>
  );

  const tagKids = (list: TagCount[], max: number, tab: LibraryTab) => (
    <>
      {list.slice(0, max).map((t) => child([t.tag], t.label, t.count))}
      {list.length > max && (
        <button type="button" className="sk more" onClick={() => toTab(tab)}>
          See all {list.length}
        </button>
      )}
    </>
  );

  const group = (k: GroupKey, icon: IconName, label: string, kids: ReactNode, hasKids: boolean) => (
    <>
      <div className={`sgh${isLib && state.tab === k && !state.tags.length ? ' on' : ''}`}>
        <button type="button" className="sn" onClick={() => toTab(k)}>
          <Ico nav={`tab:${k}`} icon={icon} />
          <span>{label}</span>
        </button>
        <button
          type="button"
          className={`tw${openGroups[k] ? ' open' : ''}`}
          aria-label={`Expand ${label}`}
          aria-expanded={openGroups[k]}
          onClick={() => setOpenGroups((g) => ({ ...g, [k]: !g[k] }))}
        >
          <Icon name="chev" />
        </button>
      </div>
      {openGroups[k] && hasKids && <div className="sc">{kids}</div>}
    </>
  );

  const years = [...(n?.years ?? [])].sort((a, b) => b.tag.localeCompare(a.tag));
  const events = n?.events ?? [];
  const fellowship = n?.groups.fellowship ?? [];
  const service = n?.groups.service ?? [];
  const popular = (n?.popular ?? []).filter((t) => t.category !== 'year').slice(0, 12);

  return (
    <>
      <aside className={`side${open ? ' open' : ''}`} id="side" aria-label="Navigation">
        {item('tab:all', 'grid', 'All photos', n?.total ?? null, isLib && state.tab === 'all' && !state.tags.length, () => toTab('all'))}
        {item('tab:picks', 'star', 'Picks', n?.picks ?? null, isLib && state.tab === 'picks', () => toTab('picks'))}
        <div className="sl">Browse</div>
        {group('years', 'cal', 'Years', tagKids(years, 10, 'years'), years.length > 0)}
        {group('regions', 'globe', 'Regions', tagKids(n?.regions ?? [], 6, 'regions'), (n?.regions.length ?? 0) > 0)}
        {group(
          'events',
          'tent',
          'Events',
          <>
            {events.slice(0, 6).map((r) => child(r.tags, r.label, r.count))}
            {events.length > 6 && (
              <button type="button" className="sk more" onClick={() => toTab('events')}>
                See all {events.length}
              </button>
            )}
          </>,
          events.length > 0,
        )}
        {group('churches', 'church', 'Churches', tagKids(n?.churches ?? [], 6, 'churches'), (n?.churches.length ?? 0) > 0)}
        {group(
          'groups',
          'users',
          'Groups',
          <>
            {fellowship.length > 0 && (
              <>
                <div className="subhead" role="heading" aria-level={3}>
                  Fellowship
                </div>
                {tagKids(fellowship, 6, 'groups')}
              </>
            )}
            {service.length > 0 && (
              <>
                <div className="subhead" role="heading" aria-level={3}>
                  Service
                </div>
                {tagKids(service, 6, 'groups')}
              </>
            )}
          </>,
          fellowship.length + service.length > 0,
        )}
        {popular.length > 0 && (
          <>
            <div className="sl">Popular tags</div>
            <div className="sidetags">
              {popular.map((t) => (
                <button
                  type="button"
                  key={t.tag}
                  className={`chip cat-${t.category}${isLib && key === t.tag ? ' on' : ''}`}
                  title={`${t.count} photos`}
                  onClick={() => toTags([t.tag])}
                >
                  #{t.tag}
                  <span className="n">{t.count}</span>
                </button>
              ))}
            </div>
          </>
        )}
        {n && (
          <div className="sfoot">
            {fmtCount(n.total)} photos · {fmtTotal(n.totalBytes)}
          </div>
        )}
      </aside>
      <div className={`scrim${open ? ' open' : ''}`} id="scrim" onClick={onClose} />
    </>
  );
}
